/**
 * The plan's self-review: the rounds the planning run's Agent ran on its own
 * plan before a person saw it.
 *
 * One closed row above "The plan" ("Reeve's self-review · 3 rounds · settled
 * on round 3 · 3 major changes made · 7m 55s"). Open, it reads as the
 * specialist roster does (`review/SpecialistsSection.tsx`): one row per round,
 * and each round opens to what it found and what it changed. Recorded facts
 * only: every count is counted off the rounds, and a review that did not end
 * the way it is meant to says so in amber.
 *
 * While the planning run is live the running round pulses and cannot open
 * (it has nothing to show yet), and the round after it waits as a dashed row.
 */
import { useState } from "react";
import type {
  PlanSelfReviewCheckKey,
  PlanSelfReviewResponse,
  PlanSelfReviewRoundView,
} from "@yagni/shared";
import { Chip } from "../ui/index.js";
import { durationLabel } from "../workers/copy.js";
import {
  SELF_REVIEW_CHANGED_HEADING,
  SELF_REVIEW_CHECK_CLEAR,
  SELF_REVIEW_CHECK_HEADING,
  SELF_REVIEW_CHECK_LABEL,
  SELF_REVIEW_CHECKS_HEADING,
  SELF_REVIEW_CHECKS_HEADING_SIX,
  SELF_REVIEW_FAILED_HEAD,
  SELF_REVIEW_FAILED_NEXT,
  SELF_REVIEW_FOOT,
  SELF_REVIEW_MAJOR_HEADING,
  SELF_REVIEW_NO_MAJOR,
  SELF_REVIEW_PLAN_UNCHANGED,
  SELF_REVIEW_ROUND_LIMIT,
  SELF_REVIEW_VERDICT_CHIP,
  selfReviewAmberNote,
  selfReviewDoneHead,
  selfReviewFallbackNote,
  selfReviewIntro,
  selfReviewLabel,
  selfReviewListChip,
  selfReviewPendingHead,
  selfReviewRoundNote,
  selfReviewRunningHead,
  selfReviewSoFar,
  selfReviewSummary,
  type SelfReviewFacts,
} from "./workCopy.js";
import "./review/reviewCase.css";

const CHECK_ORDER: readonly PlanSelfReviewCheckKey[] = [
  "halfMeasures",
  "complexity",
  "scope",
  "observability",
  "failureCases",
  "earlierWork",
];

type RoundState = "running" | "failed" | "changed" | "done";

/** A round is running only while the run is live; one left mid-round by a run that ended did not finish. */
function roundState(round: PlanSelfReviewRoundView, live: boolean): RoundState {
  if (round.status === "failed") return "failed";
  if (round.status === "reviewing" || round.status === "checking") return live ? "running" : "failed";
  return round.majorChanges.length > 0 || isRevised(round) ? "changed" : "done";
}

/** The chips "What changed in the plan" prints: the edits' own phrases, then each list's counts. */
function changeChips(round: PlanSelfReviewRoundView): { label: string; title?: string }[] {
  const changes = round.changes;
  if (!changes) return [];
  const chips: { label: string; title?: string }[] = changes.phrases.map((label) => ({ label }));
  for (const [list, diff] of Object.entries(changes.lists) as [
    keyof typeof changes.lists,
    { added: string[]; removed: string[] },
  ][]) {
    const label = selfReviewListChip(list, diff.added.length, diff.removed.length);
    if (!label) continue;
    const title = [...diff.removed.map((entry) => `− ${entry}`), ...diff.added.map((entry) => `+ ${entry}`)].join(
      "\n",
    );
    chips.push({ label, title });
  }
  return chips;
}

function isRevised(round: PlanSelfReviewRoundView): boolean {
  return changeChips(round).length > 0;
}

/** Everything the summary, the intro and the amber note count, counted once. */
export function selfReviewFacts(review: PlanSelfReviewResponse): SelfReviewFacts {
  const rounds = review.rounds;
  const last = rounds[rounds.length - 1] ?? null;
  const running =
    review.live && last && (last.status === "reviewing" || last.status === "checking") ? last : null;
  const failed =
    rounds.find((round) => round.status === "failed") ??
    (!review.live && review.outcome === "error" && last && last.status !== "done" ? last : null);
  return {
    outcome: review.outcome,
    rounds: rounds.length,
    runningRound: running?.n ?? null,
    majorChanges: rounds.reduce((sum, round) => sum + round.majorChanges.length, 0),
    checkAsks: rounds.filter((round) => round.check?.verdict === "changes_requested").length,
    failedRound: failed?.n ?? null,
    lastMadeMajor: (last?.majorChanges.length ?? 0) > 0,
    lastCheck: last?.check?.verdict ?? null,
    latestCheck: [...rounds].reverse().find((round) => round.check?.verdict)?.check?.verdict ?? null,
  };
}

/** The round running now, while the planning run is live; null otherwise. */
export function runningSelfReviewRound(review: PlanSelfReviewResponse | null): number | null {
  if (!review?.live) return null;
  return selfReviewFacts(review).runningRound;
}

function nowIso(): string {
  return new Date().toISOString();
}

function Chevron() {
  return (
    <span className="wk-chev" aria-hidden="true">
      <svg viewBox="0 0 10 10">
        <path d="M3 1.5 7 5l-4 3.5z" />
      </svg>
    </span>
  );
}

function RoundDetail({ round, live }: { round: PlanSelfReviewRoundView; live: boolean }) {
  if (roundState(round, live) === "failed") {
    return (
      <>
        <p className="sr-check-text sr-failed" data-testid={`plan-self-review-round-${round.n}-error`}>
          {round.error?.message ?? SELF_REVIEW_FAILED_HEAD}
        </p>
        {!live && <p className="sr-foot">{SELF_REVIEW_FAILED_NEXT}</p>}
      </>
    );
  }
  const chips = changeChips(round);
  return (
    <>
      {round.mode === "notes_fallback" && (
        <p className="sr-fallback" data-testid={`plan-self-review-round-${round.n}-fallback`}>
          {selfReviewFallbackNote(round.fallbackReason)}
        </p>
      )}
      <div className="sr-h">{SELF_REVIEW_MAJOR_HEADING}</div>
      {round.majorChanges.length > 0 ? (
        <ul className="sr-major">
          {round.majorChanges.map((change, index) => (
            <li key={index}>
              <span>
                {change.change}
                <span className="why">{change.why}</span>
                <span className="ev">{change.evidence}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="sr-changed">{SELF_REVIEW_NO_MAJOR}</p>
      )}
      {round.checks && (
        <>
          <div className="sr-h">
            {round.checks.earlierWork ? SELF_REVIEW_CHECKS_HEADING_SIX : SELF_REVIEW_CHECKS_HEADING}
          </div>
          <ul className="sr-checks">
            {CHECK_ORDER.map((key) => {
              const check = round.checks![key];
              if (!check) return null;
              const concern = check.verdict === "concern";
              return (
                <li
                  key={key}
                  className={"sr-check" + (concern ? " is-concern" : " is-clear")}
                  data-testid={`plan-self-review-check-${key}`}
                >
                  <span className="sr-check-name">
                    <span className="sr-check-mark" aria-hidden>
                      {concern ? "●" : "✓"}
                    </span>
                    {SELF_REVIEW_CHECK_LABEL[key]}
                  </span>
                  <span className="sr-check-text">
                    {concern ? (check.concern ?? "") : SELF_REVIEW_CHECK_CLEAR}
                    {check.evidence && (concern || key === "earlierWork") ? (
                      <span className="ev">{check.evidence}</span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div className="sr-h">{SELF_REVIEW_CHANGED_HEADING}</div>
      <p className="sr-changed">
        {chips.length > 0
          ? chips.map((chip, index) => (
              <span key={index} className="wk-planchip" {...(chip.title ? { title: chip.title } : {})}>
                {chip.label}
              </span>
            ))
          : SELF_REVIEW_PLAN_UNCHANGED}
      </p>
      {round.check?.verdict && (
        <div data-testid={`plan-self-review-round-${round.n}-check`}>
          <div className="sr-h">{SELF_REVIEW_CHECK_HEADING}</div>
          <p className="sr-changed">
            <Chip tone={round.check.verdict === "approved" ? "success" : "honesty"}>
              {SELF_REVIEW_VERDICT_CHIP[round.check.verdict]}
            </Chip>
          </p>
          {round.check.notes.length > 0 && (
            <ul className="sr-major sr-check-notes">
              {round.check.notes.map((note, index) => (
                <li key={index} className="is-open">
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

const DOT: Record<RoundState, string> = {
  running: "is-running",
  failed: "is-failed",
  changed: "is-changed",
  done: "is-done",
};

function RoundRow({
  round,
  live,
  open,
  onToggle,
}: {
  round: PlanSelfReviewRoundView;
  live: boolean;
  open: boolean;
  onToggle: () => void;
}) {
  const state = roundState(round, live);
  const expandable = state !== "running";
  const duration =
    state === "running"
      ? durationLabel(round.startedAt, nowIso())
      : round.completedAt
        ? durationLabel(round.startedAt, round.completedAt)
        : null;
  const head =
    state === "running"
      ? selfReviewRunningHead(round.n, round.status === "checking" ? "checking" : "reviewing")
      : state === "failed"
        ? SELF_REVIEW_FAILED_HEAD
        : (round.headline ?? selfReviewDoneHead(round.majorChanges.length));
  const note = selfReviewRoundNote({
    state: state === "running" ? "running" : state === "failed" ? "failed" : "done",
    majorChanges: round.majorChanges.length,
    revised: isRevised(round),
    check: round.check?.verdict ?? null,
    duration,
  });
  return (
    <li>
      <button
        type="button"
        className="rvc-roster-row"
        disabled={!expandable}
        aria-expanded={expandable ? open : undefined}
        data-testid={`plan-self-review-round-${round.n}`}
        data-state={state}
        onClick={onToggle}
      >
        <span className="rvc-roster-tri" aria-hidden>
          {expandable ? (open ? "▾" : "▸") : ""}
        </span>
        <span className={`rvc-roster-dot ${DOT[state]}`} aria-hidden />
        <span className="rvc-roster-name">Round {round.n}</span>
        <span className="rvc-roster-head">{head}</span>
        <span className="rvc-roster-note">{note}</span>
      </button>
      {open && expandable && (
        <div className="rvc-roster-detail" data-testid={`plan-self-review-round-${round.n}-detail`}>
          <RoundDetail round={round} live={live} />
        </div>
      )}
    </li>
  );
}

export function PlanSelfReview({
  review,
  agentName,
}: {
  review: PlanSelfReviewResponse | null;
  /** The planning run's Agent, as the roster names them; null when it cannot. */
  agentName: string | null;
}) {
  // Open from the start while the rounds are still landing (that is what a
  // reader is watching for); closed once the plan is on the page.
  const [open, setOpen] = useState(() => review?.live === true);
  const [openRound, setOpenRound] = useState<number | null>(null);
  if (!review || review.outcome === "none" || review.rounds.length === 0) return null;

  const facts = selfReviewFacts(review);
  const rounds = review.rounds;
  const first = rounds[0]!;
  const last = rounds[rounds.length - 1]!;
  const failedMessage = facts.failedRound
    ? (rounds.find((round) => round.n === facts.failedRound)?.error?.message ?? null)
    : null;
  const amber = selfReviewAmberNote(facts, agentName, failedMessage);
  const span = review.live
    ? durationLabel(first.startedAt, nowIso())
    : durationLabel(first.startedAt, last.completedAt ?? last.startedAt);
  const meta = span ? (review.live ? selfReviewSoFar(span) : span) : null;
  const pendingAfter =
    facts.runningRound && facts.runningRound < SELF_REVIEW_ROUND_LIMIT ? facts.runningRound : null;

  return (
    <div className="sr" data-testid="plan-self-review" data-outcome={review.outcome}>
      <button
        type="button"
        className="sr-toggle"
        aria-expanded={open}
        data-testid="plan-self-review-toggle"
        onClick={() => setOpen((value) => !value)}
      >
        <Chevron />
        <span className="sr-lab">{selfReviewLabel(agentName)}</span>
        <span
          className={"sr-sum" + (amber ? " is-honesty" : "")}
          data-testid="plan-self-review-summary"
        >
          {selfReviewSummary(facts)}
        </span>
        {meta && <span className="sr-meta">{meta}</span>}
      </button>
      {open && (
        <div className="sr-body" data-testid="plan-self-review-body">
          <p className="sr-intro">{selfReviewIntro(facts, agentName)}</p>
          {amber && (
            <div className="sr-note" data-testid="plan-self-review-note">
              {amber}
            </div>
          )}
          <ul className="rvc-roster">
            {rounds.map((round) => (
              <RoundRow
                key={round.n}
                round={round}
                live={review.live}
                open={openRound === round.n}
                onToggle={() => setOpenRound((current) => (current === round.n ? null : round.n))}
              />
            ))}
            {pendingAfter != null && (
              <li>
                <button
                  type="button"
                  className="rvc-roster-row"
                  disabled
                  data-testid={`plan-self-review-round-${pendingAfter + 1}`}
                  data-state="pending"
                >
                  <span className="rvc-roster-tri" aria-hidden />
                  <span className="rvc-roster-dot is-pending" aria-hidden />
                  <span className="rvc-roster-name is-pending">Round {pendingAfter + 1}</span>
                  <span className="rvc-roster-head">{selfReviewPendingHead(pendingAfter)}</span>
                </button>
              </li>
            )}
          </ul>
          {!review.live && <p className="sr-foot">{SELF_REVIEW_FOOT}</p>}
        </div>
      )}
    </div>
  );
}
